import { collection, query, where, getDocs } from 'firebase/firestore';
import { db } from '../firebase';
import { updateAccount } from './firestoreService';

/**
 * Getting Started progress for pending-start clients.
 *
 * A client who hasn't reached their program start date lands on
 * PortalPendingStartGate, where they rank their priorities, set up their
 * profile, and walk a page-by-page tour. Everything they do there has to be
 * visible to the success manager on CRM ▸ Fulfillment ▸ Kickoff — otherwise the
 * kickoff call starts from scratch.
 *
 * Four separate stores feed that view:
 *   1. accounts/{id}.departmentAssessmentItems / departmentPriorityOrder
 *      — the priorities, written by KickoffAssessmentSections (already shared).
 *   2. accounts/{id}.kickoffTourProgress — which tour stops they've opened.
 *   3. accounts/{id}.kickoffCallBooking — that they booked their kickoff call.
 *   4. users/{uid} + the account doc — what their profile setup actually holds.
 *
 * This module owns (2) and (3), and derives (4).
 */

/** Tour stops are keyed by id, never index — TOUR_STOPS order is not stable. */
export async function saveKickoffTourProgress(accountId, visitedStopIds, totalStops) {
  if (!accountId) return null;
  const visited = [...new Set((visitedStopIds || []).filter(Boolean))];
  const progress = {
    visitedStops: visited,
    totalStops: totalStops ?? visited.length,
    lastVisitedAt: new Date().toISOString(),
    completedAt: totalStops && visited.length >= totalStops ? new Date().toISOString() : null,
  };
  await updateAccount(accountId, { kickoffTourProgress: progress });
  return progress;
}

/**
 * Records that the client booked their kickoff call from Getting Started.
 * Written off Calendly's `event_scheduled` postMessage (or their manual "I've
 * already booked this" confirmation), so `source` says which one it was.
 */
export async function saveKickoffCallBooking(accountId, details = {}) {
  if (!accountId) return null;
  const booking = {
    scheduledAt: new Date().toISOString(),
    eventUri: details.eventUri || '',
    inviteeUri: details.inviteeUri || '',
    bookedByEmail: details.bookedByEmail || '',
    source: details.source || 'calendly',
  };
  await updateAccount(accountId, { kickoffCallBooking: booking });
  return booking;
}

/** Portal users belong to an account via either `accountId` or the older `clientId`. */
export async function fetchAccountPortalUsers(accountId) {
  if (!accountId) return [];
  const usersRef = collection(db, 'users');
  const [byAccount, byClient] = await Promise.all([
    getDocs(query(usersRef, where('accountId', '==', accountId))),
    getDocs(query(usersRef, where('clientId', '==', accountId))),
  ]);
  const byId = new Map();
  [...byAccount.docs, ...byClient.docs].forEach((d) => byId.set(d.id, { id: d.id, ...d.data() }));
  return [...byId.values()];
}

const fullName = (u) => [u.firstName, u.lastName].filter(Boolean).join(' ').trim();
const listNames = (users) => {
  const names = users.map(fullName).filter(Boolean);
  if (names.length === 0) return '';
  if (names.length <= 2) return names.join(' and ');
  return `${names.slice(0, 2).join(', ')} +${names.length - 2} more`;
};

/**
 * Which Profile Setup cards the client has actually filled in, with a one-line
 * summary of what they entered. Keys match KickoffOverview's profileCards.
 *
 * Each card is "done" when at least one portal user on the account has supplied
 * that piece — a two-person team where only the owner filled it in still counts,
 * since the success manager only needs to know whether it's covered.
 */
export function deriveProfileSetupStatus(account = {}, users = []) {
  const acct = account || {};

  const named = users.filter((u) => fullName(u));
  const withPhone = users.filter((u) => String(u.phone || '').trim());
  const personalDetail = named.length
    ? `${listNames(named)}${withPhone.length ? ' · phone added' : ' · no phone yet'}`
    : 'No names entered yet';

  const companyBits = [];
  if (acct.company) companyBits.push(acct.company);
  if (acct.website) companyBits.push(acct.website);
  if (acct.companyLogo) companyBits.push('logo');
  if (acct.address?.street || acct.address?.city) companyBits.push('address');
  const companyDone = Boolean(acct.company) && companyBits.length > 1;

  // darkMode is written whenever the profile form is saved, so its presence —
  // not its value — is the signal that they got through that screen.
  const themeUser = users.find((u) => typeof u.darkMode === 'boolean');
  // planKey is 'unknown' on most live accounts, so it proves nothing on its own —
  // an actual Stripe subscription or a recorded payment is the real signal.
  const billing = acct.billing || {};
  const realPlanKey = billing.planKey && billing.planKey !== 'unknown' ? billing.planKey : '';
  const billingDone = Boolean(billing.stripeSubscriptionId || realPlanKey || Number(billing.lifetimePaid) > 0);
  const billingBits = [];
  if (realPlanKey) billingBits.push(realPlanKey);
  if (billing.status) billingBits.push(String(billing.status).replace(/_/g, ' '));

  const notifUsers = users.filter((u) => u.notifications && Object.values(u.notifications).some(Boolean));
  const notifCount = notifUsers.length
    ? Math.max(...notifUsers.map((u) => Object.values(u.notifications).filter(Boolean).length))
    : 0;

  const connected = new Set();
  users.forEach((u) => {
    Object.entries(u.integrations || {}).forEach(([key, val]) => {
      if (val?.connected) connected.add(key);
    });
  });

  return {
    personalInfo: { done: named.length > 0, detail: personalDetail },
    companyInfo: {
      done: companyDone,
      detail: companyBits.length ? companyBits.join(' · ') : 'Nothing entered yet',
    },
    darkMode: {
      done: Boolean(themeUser),
      detail: themeUser ? (themeUser.darkMode ? 'Dark mode' : 'Light mode') : 'Not set yet',
    },
    billing: {
      done: billingDone,
      detail: billingBits.length ? billingBits.join(' · ') : 'No subscription on file',
    },
    notifications: {
      done: notifCount > 0,
      detail: notifCount > 0 ? `${notifCount} alert${notifCount === 1 ? '' : 's'} turned on` : 'None turned on yet',
    },
    integrations: {
      done: connected.size > 0,
      detail: connected.size > 0 ? [...connected].join(', ') : 'Nothing connected yet',
    },
  };
}
